import React, { useEffect, useState } from 'react';
import S3Upload from 'react-s3-uploader/s3upload';
import { S3Response } from 'react-s3-uploader';
import Dropzone from 'react-dropzone';
import { CloudUploadOutlined } from '@mui/icons-material';

import { gray, orange, primaryBlue } from 'utils/color-styles';
import { imageTypes } from 'retired/FileUpload/utils';

import { File } from './File';
import { ImagePreview } from './ImagePreview';
import { getGetSignedUrl } from './getGetSignedUrl';

type S3Acl = 'private' | 'public-read';
type S3Bucket = 'primary' | 'secure';

interface DropzoneFile extends File {
  preview?: string;
}

export interface FileWithMeta {
  file: DropzoneFile;
  status: 'uploaded' | 'error' | 'uploading';
  errorMessage?: string;
  publicUrl?: string;
  signedUrl?: string;
}

interface Props {
  maxSize?: number;
  s3Acl: S3Acl;
  s3Bucket: S3Bucket;
  acceptedTypes?: string[];
  multiple?: boolean;
  errorDisplayDuration?: number;
  imagePreview?: boolean;
  previousFileUrl?: string;
  onChange?: (uploadedFiles: FileWithMeta[], uploadInProgress: boolean) => void;
  s3ObjectTags?: string;
  objectPrefix?: string;
  height?: 'sm' | 'md' | 'lg';
}

export const FilesUpload = ({
  maxSize = 1024 * 1024 * 10,
  s3Acl = 'public-read',
  s3Bucket = 'primary',
  acceptedTypes = [...imageTypes, 'application/pdf'],
  multiple = true,
  errorDisplayDuration = 2500,
  imagePreview = false,
  previousFileUrl,
  onChange,
  s3ObjectTags,
  objectPrefix,
  height = 'lg',
}: Props) => {
  const [filesWithMeta, setFilesWithMeta] = useState<FileWithMeta[]>([]);
  const [errorCountdown, setErrorCountdown] = useState(0);
  const [errorPresent, setErrorPresent] = useState(false);

  useEffect(() => {
    const uploadInProgress = filesWithMeta.some((f) => f.status === 'uploading');
    onChange?.(
      filesWithMeta.filter((f) => f.status === 'uploaded'),
      uploadInProgress
    );
  }, [filesWithMeta]);

  // Check for errors and set flag to start countdown
  useEffect(() => {
    if (filesWithMeta.some((f) => f.status === 'error')) {
      setErrorPresent(true);
    } else {
      setErrorPresent(false);
      setErrorCountdown(0);
    }
  }, [filesWithMeta]);

  // Manage the countdown
  useEffect(() => {
    let interval = null;
    let timeout = null;

    if (errorPresent) {
      setErrorCountdown(0);
      interval = setInterval(() => {
        setErrorCountdown((prev) => (prev < 100 ? prev + 100 / (errorDisplayDuration / 100) : 100));
      }, 100);

      timeout = setTimeout(() => {
        setFilesWithMeta((fm) => fm.filter((f) => f.status !== 'error'));
        setErrorPresent(false);
        if (interval) {
          clearInterval(interval);
        }
      }, errorDisplayDuration);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [errorPresent, errorDisplayDuration]);

  const uploadFiles = (newFiles: DropzoneFile[]) => {
    setFilesWithMeta((_filesWithMeta) => [
      ..._filesWithMeta,
      ...newFiles.map((newFile) => ({ file: newFile, status: 'uploading' as const })),
    ]);
    const uploadRequestHeaders: { 'x-amz-acl': string; 'x-amz-tagging'?: string } = {
      'x-amz-acl': s3Acl,
    };
    if (s3ObjectTags) {
      uploadRequestHeaders['x-amz-tagging'] = s3ObjectTags;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, no-new
    new S3Upload({
      files: newFiles,
      getSignedUrl: getGetSignedUrl({
        acl: s3Acl,
        bucket: s3Bucket,
        objectPrefix,
        prefixCompanyCode: true,
        prefixUserId: true,
      }),
      contentDisposition: 'auto',
      uploadRequestHeaders,
      onFinishS3Put: (info: S3Response, file: DropzoneFile) => {
        setFilesWithMeta((_filesWithMeta) =>
          _filesWithMeta.map((f) =>
            f.file.preview === file.preview ? { file, status: 'uploaded', ...info } : f
          )
        );
      },
      onError: (_status: string, file: DropzoneFile) => {
        setFilesWithMeta((_filesWithMeta) =>
          _filesWithMeta.map((f) =>
            f.file.preview === file.preview ? { ...f, status: 'error' } : f
          )
        );
      },
    });
  };

  const heightPx = height === 'sm' ? 80 : height === 'md' ? 110 : 140;

  return (
    <>
      <div
        style={{
          display: imagePreview && (filesWithMeta.length || previousFileUrl) ? 'flex' : 'block',
          justifyContent: 'space-between',
        }}
      >
        {(!filesWithMeta.length || multiple) && (
          <Dropzone
            onDrop={(files: DropzoneFile[], rejectedFiles: DropzoneFile[]) => {
              setFilesWithMeta((_filesWithMeta) =>
                _filesWithMeta.filter((f) => f.status !== 'error')
              );
              if (rejectedFiles?.length > 0) {
                setFilesWithMeta((_filesWithMeta) => [
                  ..._filesWithMeta,
                  ...rejectedFiles.map((rejectedFile) => {
                    let errorMessage = 'Invalid file.';
                    if (maxSize && rejectedFile?.size && rejectedFile.size > maxSize) {
                      errorMessage = 'File size is too large. Try a smaller one.';
                    } else if (
                      rejectedFile?.type &&
                      acceptedTypes &&
                      !acceptedTypes.includes(rejectedFile.type)
                    ) {
                      errorMessage = 'Invalid file type.';
                    } else if (!multiple) {
                      errorMessage = 'Only one file can be uploaded at a time.';
                    }
                    return {
                      file: rejectedFile,
                      status: 'error' as const,
                      errorMessage,
                    };
                  }),
                ]);
              }
              uploadFiles(files);
            }}
            maxSize={maxSize}
            multiple={multiple}
            accept={acceptedTypes}
            style={{
              height: `${heightPx}px`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 4,
              // @TODO-DP: hardcode the colours for now as they are not in theme
              border: `1px dashed rgba(81, 40, 170, 0.30)`,
              background: 'rgba(81, 40, 170, 0.04)',
              color: primaryBlue,
              textAlign: 'center',
              fontSize: 12,
              flexGrow: imagePreview && (Boolean(filesWithMeta.length) || previousFileUrl) ? 2 : 1,
            }}
            activeStyle={{ border: `2px solid ${gray}` }}
            rejectStyle={{ border: `2px dashed ${orange}` }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: height === 'sm' ? 'row' : 'column',
                gap: height === 'sm' ? 12 : 0,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CloudUploadOutlined style={{ fontSize: 48 }} />
              <div>Drag and drop or click here to upload</div>
            </div>
          </Dropzone>
        )}

        {imagePreview && !multiple && (Boolean(filesWithMeta.length) || previousFileUrl) && (
          <ImagePreview
            fileUrl={previousFileUrl}
            fileWithMeta={filesWithMeta[0]}
            onDelete={() => {
              setFilesWithMeta((filesInfo) =>
                filesInfo.filter((f) => f.file.preview !== filesWithMeta[0].file.preview)
              );
            }}
          />
        )}
      </div>
      {filesWithMeta.map((fileWithMeta) => (
        <File
          errorCountdown={errorCountdown}
          key={fileWithMeta.file.preview}
          fileWithMeta={fileWithMeta}
          onDelete={() => {
            setFilesWithMeta((filesInfo) =>
              filesInfo.filter((f) => f.file.preview !== fileWithMeta.file.preview)
            );
          }}
        />
      ))}
    </>
  );
};
